import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Calculator, Euro, Home, Ruler, TrendingUp, TrendingDown, Info,
  Settings, Building2, ChevronRight, ChevronLeft, Check, AlertTriangle,
  Flame, Zap, User, Calendar, MapPin, ArrowRight, FileText, ThermometerSun,
  FolderPlus, Phone, Mail, Hash, Building, ToggleLeft, ToggleRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { buildTimelinesForProgram, TimelineDisplay } from "@/components/ProjectTimeline";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

// Coefficients par surface
const COEFFICIENTS_SURFACE = [
  { min: 0, max: 34, coef: 0.40 },
  { min: 35, max: 59, coef: 0.50 },
  { min: 60, max: 89, coef: 0.80 },
  { min: 90, max: 109, coef: 1.00 },
  { min: 110, max: 129, coef: 1.20 },
  { min: 130, max: 99999, coef: 1.30 }
];

const CEE_PAR_CLASSES = {
  2: { mwhCumac: 723 },
  3: { mwhCumac: 892 },
  4: { mwhCumac: 1138 }
};

const TVA = 0.055;

const TRAVAUX_DEFAUT = {
  combles: { label: "Isolation combles", materiel: 12, pose: 8, unite: "m²" },
  planchersBas: { label: "Isolation plancher bas", materiel: 12, pose: 10, unite: "m²" },
  rampant: { label: "Rampant", materiel: 0, pose: 50, unite: "m²" },
  murs: { label: "Isolation murs intérieurs", materiel: 0, pose: 50, unite: "m²" },
  fenetres: { label: "Fenêtres", materiel: 450, pose: 0, unite: "unité" },
  ballonHybride: { label: "Ballon Hybride", materiel: 450, pose: 350, unite: "unité" },
  radiateurs: { label: "Radiateurs électriques", materiel: 100, pose: 70, unite: "unité" },
  monoSplit: { label: "Mono Split (1 split)", materiel: 800, pose: 500, unite: "unité" },
  splitSupp: { label: "Split supplémentaire", materiel: 150, pose: 500, unite: "unité" },
  vmcSimple: { label: "VMC Simple flux", materiel: 0, pose: 600, unite: "unité" },
  vmcDouble: { label: "VMC Double flux", materiel: 0, pose: 1950, unite: "unité" },
  vmcDoubleCompact: { label: "VMC Double flux compact", materiel: 0, pose: 700, unite: "unité" },
  thermostat: { label: "Kit Thermostat connecté", materiel: 0, pose: 200, unite: "unité" },
  coutsFixes: { label: "Coûts fixes", materiel: 0, pose: 545, unite: "" }
};

// Constantes MPR
const MONTANT_KWHC = {
  H1: { "S < 70": 327600, "70 <= S < 90": 458640, "90 <= S": 655200 },
  H2: { "S < 70": 273000, "70 <= S < 90": 382200, "90 <= S": 546000 },
  H3: { "S < 70": 191100, "70 <= S < 90": 267540, "90 <= S": 382200 }
};

const PRIX_RACHAT = { bleu: 0.0125, jaune: 0.0074 };

const MPR_AIDES = {
  PAC: { bleu: 5000, jaune: 4000 },
  "PAC/SSC": { bleu: 15000, jaune: 12000 },
  "PAC + CESI": { bleu: 9000, jaune: 7000 }
};

const TAUX_FINANCEMENT_MPR = 0.12;

function getRegies(mode, precarite, surfaceNum) {
  if (mode === "lead") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (precarite === "jaune") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (surfaceNum < 70) return { r174: 1000, rMPR: 2000, r174Cum: 1000, rMPRCum: 2000 };
  if (surfaceNum <= 130) return { r174: 1500, rMPR: 2500, r174Cum: 1500, rMPRCum: 2500 };
  return { r174: 2000, rMPR: 3000, r174Cum: 2000, rMPRCum: 3000 };
}

// Profils et Périodes
const PROFILES = [
  { id: "bailleur_sci", label: "Bailleur en SCI (personne morale)", icon: Building2, order: ["174", "MPR"] },
  { id: "proprietaire_occupant", label: "Propriétaire occupant (résidence principale)", icon: Home, order: ["MPR", "174"] },
  { id: "residence_secondaire", label: "Propriétaire résidence secondaire", icon: Home, order: ["174", "MPR"] },
];

const PERIODS = [
  { id: "before_june_2025", label: "Avant le 15 juin 2025" },
  { id: "june_2025_jan_2026", label: "Du 15 juin 2025 au 16 janvier 2026" },
  { id: "from_jan_2026", label: "À partir du 17 janvier 2026" }
];

const DPE_CLASSES = ["A", "B", "C", "D", "E", "F", "G"];
const DPE_COLORS = { A: "bg-green-600", B: "bg-lime-500", C: "bg-yellow-400", D: "bg-amber-400", E: "bg-orange-500", F: "bg-red-500", G: "bg-red-700" };

// Logique d'éligibilité
function check174Eligibility(profile, period, dpeActuel, ageLogement) {
  if (profile === "bailleur_sci") return { eligible: true, reason: "Les bailleurs personnes morales (SCI) sont éligibles à la 174 pour toutes les périodes." };
  if (profile === "residence_secondaire") {
    if (period === "from_jan_2026") return { eligible: false, reason: "Les propriétaires de résidence secondaire ne sont plus éligibles à la 174 à partir du 17/01/2026." };
    return { eligible: true, reason: period === "before_june_2025" ? "Éligible si occupation < 6 mois/an." : "Éligible si occupation < 8 mois/an." };
  }
  if (profile === "proprietaire_occupant") {
    if (period === "before_june_2025") return { eligible: false, reason: "Les propriétaires occupants en résidence principale ne sont pas éligibles avant le 15/06/2025." };
    if (period === "june_2025_jan_2026") {
      if (ageLogement === "2_to_15") return { eligible: true, reason: "Éligible car le logement a entre 2 et 15 ans." };
      return { eligible: false, reason: "Le logement doit avoir entre 2 et 15 ans pour cette période." };
    }
    if (ageLogement === "2_to_15") return { eligible: true, reason: "Éligible car le logement a entre 2 et 15 ans." };
    if (ageLogement === "more_than_15") {
      if (["A", "B", "C", "D"].includes(dpeActuel)) return { eligible: true, reason: "Éligible car le logement a +15 ans avec un DPE " + dpeActuel + " (A, B, C ou D requis)." };
      return { eligible: false, reason: "Logement de +15 ans : DPE " + dpeActuel + " non éligible. Seuls les DPE A, B, C, D sont acceptés." };
    }
    return { eligible: false, reason: "Le logement doit avoir au moins 2 ans." };
  }
  return { eligible: false, reason: "Profil non reconnu." };
}

function checkMPREligibility(chauffage) {
  if (chauffage === "Gaz" || chauffage === "Fioul") return { eligible: true, reason: "Éligible au MPR car le chauffage actuel est au " + chauffage + "." };
  return { eligible: false, reason: "Le programme MPR nécessite un chauffage au gaz ou au fioul. Chauffage actuel : " + chauffage + "." };
}

// Helpers
function getCoefficient(surface) {
  const c = COEFFICIENTS_SURFACE.find(c => surface >= c.min && surface <= c.max);
  return c ? c.coef : 1.30;
}

function getMPRSurfaceKey(surface) {
  if (surface < 70) return "S < 70";
  if (surface < 90) return "70 <= S < 90";
  return "90 <= S";
}

function getDPEJump(dpeActuel, dpeVise) {
  const idx1 = DPE_CLASSES.indexOf(dpeActuel);
  const idx2 = DPE_CLASSES.indexOf(dpeVise);
  return idx1 - idx2;
}

// Composant principal
export default function SimulateurProfil() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCreateMode = searchParams.get("mode") === "create";
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);

  // Step 1: Profil
  const [profil, setProfil] = useState("");
  const [periodeMPRAvant, setPeriodeMPRAvant] = useState(false);
  const periode = periodeMPRAvant ? "before_jan_2026" : "from_jan_2026";

  // Step 2: Infos du bien
  const [surface, setSurface] = useState(100);
  const [dpeActuel, setDpeActuel] = useState("F");
  const [dpeVise, setDpeVise] = useState("C");
  const [zone, setZone] = useState("H1");
  const [chauffage, setChauffage] = useState("Gaz");
  const [precarite, setPrecarite] = useState("bleu");
  const [ageLogement, setAgeLogement] = useState("more_than_15");

  // Step 174 config
  const [scenario174, setScenario174] = useState(3);
  const [valorisation, setValorisation] = useState(13);
  const [quantites, setQuantites] = useState({
    combles: 70, planchersBas: 70, rampant: 0, murs: 0, fenetres: 2,
    ballonHybride: 0, radiateurs: 0, monoSplit: 1, splitSupp: 0,
    vmcSimple: 0, vmcDouble: 0, vmcDoubleCompact: 0, thermostat: 0, coutsFixes: 1
  });
  const [couts174, setCouts174] = useState(
    Object.fromEntries(Object.entries(TRAVAUX_DEFAUT).map(([k, v]) => [k, { materiel: v.materiel, pose: v.pose }]))
  );
  const [regieMode, setRegieMode] = useState("regie");

  // Step MPR config
  const [typeChantier, setTypeChantier] = useState("PAC/SSC");
  const [chargesMPR, setChargesMPR] = useState({
    marchandise_pac_ssc: 6580, marchandise_pac: 3000, marchandise_pac_cesi: 4917,
    sous_traitant_pac_ssc: 4200, sous_traitant_pac: 2000, sous_traitant_pac_cesi: 3917,
    admin: 80
  });

  // Step Création dossier
  const [dossierForm, setDossierForm] = useState({
    client_name: "", client_email: "", client_phone: "", code_postal: "",
    adresse_logement: "",
    organisme_bailleur: "", nom_representant: "", tel_representant: "",
    fixe_representant: "", email_representant: "", adresse_bailleur: "",
    nom_residence: "", adresse_residence: "", coordonnees_gardien: "",
    nombre_logements: 1, type_logements: "appartement", notes: ""
  });

  useEffect(() => {
    axios.get(`${API}/profiles`).then(r => setProfiles(r.data)).catch(() => {});
  }, []);

  // Éligibilité
  const elig174 = useMemo(() => check174Eligibility(profil, periode, dpeActuel, ageLogement), [profil, periode, dpeActuel, ageLogement]);
  const eligMPR = useMemo(() => checkMPREligibility(chauffage), [chauffage]);

  const profileData = PROFILES.find(p => p.id === profil);
  const programOrder = useMemo(() => profileData?.order || ["174", "MPR"], [profileData]);

  // Résultats 174
  const isJauneProfil = precarite === "jaune";

  // Régies calculées
  const isCumule = elig174.eligible && eligMPR.eligible;
  const regies = getRegies(regieMode, precarite, surface);
  const regie174 = isCumule ? regies.r174Cum : regies.r174;
  const regieMPR = isCumule ? regies.rMPRCum : regies.rMPR;

  const results174 = useMemo(() => {
    if (!elig174.eligible) return null;
    const coef = getCoefficient(surface);
    const classJump = getDPEJump(dpeActuel, dpeVise);
    const effectiveScenario = Math.max(2, Math.min(4, classJump));
    const ceeData = CEE_PAR_CLASSES[effectiveScenario] || CEE_PAR_CLASSES[scenario174];
    const caTTCBrut = ceeData.mwhCumac * coef * valorisation;
    const caTTC = caTTCBrut;
    const caHT = caTTC / (1 + TVA);
    let coutTravaux = 0;
    Object.keys(quantites).forEach(key => {
      if (quantites[key] > 0) coutTravaux += (couts174[key].materiel + couts174[key].pose) * quantites[key];
    });
    coutTravaux += regie174;
    const marge = caHT - coutTravaux;
    return { coef, mwhCumac: ceeData.mwhCumac, classJump: effectiveScenario, caTTCBrut: Math.round(caTTCBrut * 100) / 100, caTTC: Math.round(caTTC * 100) / 100, caHT: Math.round(caHT * 100) / 100, coutTravaux: Math.round(coutTravaux * 100) / 100, marge: Math.round(marge * 100) / 100, regie: regie174, isJaune: isJauneProfil };
  }, [elig174.eligible, surface, dpeActuel, dpeVise, scenario174, valorisation, quantites, couts174, regie174, isJauneProfil]);

  // Résultats MPR
  const resultsMPR = useMemo(() => {
    if (!eligMPR.eligible) return null;
    const surfKey = getMPRSurfaceKey(surface);
    const montantKwhc = MONTANT_KWHC[zone][surfKey];
    const prixRachat = PRIX_RACHAT[precarite];
    const cee = montantKwhc * prixRachat;
    const mpr = MPR_AIDES[typeChantier]?.[precarite] || 0;
    let totalCharges = 0;
    if (typeChantier === "PAC") totalCharges = chargesMPR.marchandise_pac + chargesMPR.sous_traitant_pac + regieMPR + chargesMPR.admin;
    else if (typeChantier === "PAC/SSC") totalCharges = chargesMPR.marchandise_pac_ssc + chargesMPR.sous_traitant_pac_ssc + regieMPR + chargesMPR.admin;
    else totalCharges = chargesMPR.marchandise_pac_cesi + chargesMPR.sous_traitant_pac_cesi + regieMPR + chargesMPR.admin;
    const finMPR = mpr * TAUX_FINANCEMENT_MPR;
    const totalAides = mpr + cee;
    let marge;
    if (periodeMPRAvant) {
      // Avant 31/12/25 : marge fixe
      marge = precarite === "bleu" ? 6821 : 3321;
    } else {
      marge = (totalAides / 1.055) - totalCharges - finMPR;
    }
    return { montantKwhc, cee: Math.round(cee * 100) / 100, mpr, finMPR: Math.round(finMPR * 100) / 100, totalAides: Math.round(totalAides * 100) / 100, totalCharges, marge: Math.round(marge * 100) / 100, regieMPR };
  }, [eligMPR.eligible, surface, zone, precarite, typeChantier, chargesMPR, regieMPR, periodeMPRAvant]);

  // Étapes dynamiques
  const steps = useMemo(() => {
    const s = [
      { id: "profil", label: "Profil" },
      { id: "bien", label: "Informations du bien" },
    ];
    if (profil) {
      const first = programOrder[0];
      const second = programOrder[1];
      if (first === "174" && elig174.eligible) s.push({ id: "174", label: "Programme 174" });
      if (first === "MPR" && eligMPR.eligible) s.push({ id: "MPR", label: "Programme MPR" });
      if (second === "174" && elig174.eligible) s.push({ id: "174", label: "Programme 174" });
      if (second === "MPR" && eligMPR.eligible) s.push({ id: "MPR", label: "Programme MPR" });
    }
    s.push({ id: "recap", label: "Récapitulatif" });
    s.push({ id: "dossier", label: "Créer le dossier" });
    return s;
  }, [profil, programOrder, elig174.eligible, eligMPR.eligible]);

  const currentStepId = steps[step]?.id || "profil";
  const canGoNext = () => {
    if (currentStepId === "profil") return !!profil;
    if (currentStepId === "bien") return surface > 0 && dpeActuel && dpeVise && zone && chauffage && ageLogement;
    return true;
  };

  const goNext = () => { if (step < steps.length - 1 && canGoNext()) setStep(step + 1); };
  const goPrev = () => { if (step > 0) setStep(step - 1); };

  const safeStep = Math.min(step, steps.length - 1);
  if (safeStep !== step) setStep(safeStep);

  // Determine programme type
  const programmeType = isCumule ? "cumule" : (elig174.eligible ? "174" : typeChantier);

  const canCreateDossier = () => dossierForm.client_name.trim().length > 0;

  const handleCreateDossier = async () => {
    setLoading(true);
    try {
      const payload = {
        programme: programmeType,
        profile: profil === "bailleur_sci" ? "sci" : profil === "proprietaire_occupant" ? "proprietaire_occupant" : "bailleur",
        client_name: dossierForm.client_name,
        client_email: dossierForm.client_email,
        client_phone: dossierForm.client_phone,
        code_postal: dossierForm.code_postal,
        adresse_logement: dossierForm.adresse_logement,
        zone: zone,
        metrage: surface,
        precarite: precarite.toUpperCase(),
        notes: dossierForm.notes,
        nombre_logements: dossierForm.nombre_logements,
        type_logements: dossierForm.type_logements,
        surfaces_logements: Array(dossierForm.nombre_logements).fill(surface),
        scenario_classes: scenario174 || 2,
      };
      // Champs bailleur/SCI
      if (profil === "bailleur_sci") {
        payload.organisme_bailleur = dossierForm.organisme_bailleur;
        payload.nom_representant = dossierForm.nom_representant;
        payload.tel_representant = dossierForm.tel_representant;
        payload.fixe_representant = dossierForm.fixe_representant;
        payload.email_representant = dossierForm.email_representant;
        payload.adresse_bailleur = dossierForm.adresse_bailleur;
        payload.nom_residence = dossierForm.nom_residence;
        payload.adresse_residence = dossierForm.adresse_residence;
        payload.coordonnees_gardien = dossierForm.coordonnees_gardien;
      }
      // 174 fields
      if (elig174.eligible) {
        payload.nombre_logements = payload.nombre_logements || 1;
        payload.surfaces_logements = payload.surfaces_logements || [surface];
        payload.scenario_classes = scenario174;
      }
      // MPR fields for cumule
      if (isCumule) {
        payload.programme_mpr = typeChantier;
        // Pass simulator-calculated values
        const marge174 = results174?.marge || 0;
        const margeMPR = resultsMPR?.marge || 0;
        payload.sim_marge_prevue = marge174 + margeMPR;
        payload.sim_total_aides = (results174?.caTTC || 0) + (resultsMPR?.totalAides || 0);
        payload.sim_marge_174 = marge174;
        payload.sim_marge_mpr = margeMPR;
        payload.sim_mpr_calcule = resultsMPR?.mpr || 0;
        payload.sim_cee_calcule = resultsMPR?.cee || 0;
        payload.montant_devis_174 = results174?.caTTC || 0;
      } else if (elig174.eligible) {
        // Single 174 - pass simulator values
        payload.sim_marge_prevue = results174?.marge || 0;
        payload.sim_total_aides = results174?.caTTC || 0;
        payload.sim_marge_174 = results174?.marge || 0;
        payload.montant_devis_174 = results174?.caTTC || 0;
      } else {
        // Single MPR - pass simulator values
        payload.sim_marge_prevue = resultsMPR?.marge || 0;
        payload.sim_total_aides = resultsMPR?.totalAides || 0;
        payload.sim_marge_mpr = resultsMPR?.marge || 0;
        payload.sim_mpr_calcule = resultsMPR?.mpr || 0;
        payload.sim_cee_calcule = resultsMPR?.cee || 0;
      }
      const token = localStorage.getItem("token");
      const { data } = await axios.post(`${API}/dossiers`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dossier créé avec succès !");
      navigate(`/dossiers/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du dossier");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3" data-testid="simulateur-profil-title">
          <User className="w-8 h-8 text-primary" />
          {isCreateMode ? "Nouveau dossier Op. Cumulée" : "Simulateur par profil"}
        </h1>
        <p className="text-muted-foreground mt-1">
          Simulation guidée selon votre profil client - Programmes 174 et MPR
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="wizard-stepper">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center">
            <button
              onClick={() => i <= step && setStep(i)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all",
                i === step ? "bg-primary text-primary-foreground shadow-md" :
                i < step ? "bg-primary/10 text-primary cursor-pointer hover:bg-primary/20" :
                "bg-muted text-muted-foreground"
              )}
              data-testid={`step-${s.id}`}
            >
              <span className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                i === step ? "bg-card text-primary" :
                i < step ? "bg-primary text-white" :
                "bg-muted-foreground/30 text-muted-foreground"
              )}>
                {i < step ? <Check className="w-3 h-3" /> : i + 1}
              </span>
              {s.label}
            </button>
            {i < steps.length - 1 && <ChevronRight className="w-4 h-4 text-muted-foreground mx-1 shrink-0" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="min-h-[500px]">
        {currentStepId === "profil" && <StepProfil {...{ profil, setProfil, periodeMPRAvant, setPeriodeMPRAvant }} />}
        {currentStepId === "bien" && (
          <StepBien {...{ surface, setSurface, dpeActuel, setDpeActuel, dpeVise, setDpeVise, zone, setZone, chauffage, setChauffage, precarite, setPrecarite, ageLogement, setAgeLogement, elig174, eligMPR, programOrder }} />
        )}
        {currentStepId === "174" && (
          <Step174 {...{ surface, scenario174, setScenario174, valorisation, setValorisation, quantites, setQuantites, couts174, setCouts174, results174, dpeActuel, dpeVise, isCreateMode, regie174, regieMode, setRegieMode, isJaune: isJauneProfil, precarite }} />
        )}
        {currentStepId === "MPR" && (
          <StepMPR {...{ zone, surface, precarite, setPrecarite, typeChantier, setTypeChantier, chargesMPR, setChargesMPR, resultsMPR, isCreateMode, regieMPR, regieMode, setRegieMode, periode }} />
        )}
        {currentStepId === "recap" && (
          <StepRecap {...{ profil, periode, surface, dpeActuel, dpeVise, zone, chauffage, precarite, ageLogement, elig174, eligMPR, results174, resultsMPR, programOrder, typeChantier, valorisation, scenario174, quantites, couts174, chargesMPR, regieMPR, regieMode, isCreateMode }} />
        )}
        {currentStepId === "dossier" && (
          <StepDossier {...{ profil, dossierForm, setDossierForm, profiles, elig174, eligMPR }} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between border-t border-border pt-4 pb-12" data-testid="wizard-nav">
        <Button variant="outline" onClick={goPrev} disabled={step === 0} className="rounded-lg" data-testid="btn-prev">
          <ChevronLeft className="w-4 h-4 mr-2" /> Précédent
        </Button>
        <span className="text-sm text-muted-foreground">Étape {step + 1} / {steps.length}</span>
        {currentStepId === "dossier" ? (
          <Button onClick={handleCreateDossier} disabled={loading || !canCreateDossier()} className="rounded-lg bg-emerald-600 hover:bg-emerald-700" data-testid="btn-create-dossier">
            {loading ? "Création..." : "Créer le dossier"}
            <FolderPlus className="w-4 h-4 ml-2" />
          </Button>
        ) : step < steps.length - 1 ? (
          <Button onClick={goNext} disabled={!canGoNext()} className="rounded-lg" data-testid="btn-next">
            Suivant <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button variant="outline" onClick={() => setStep(0)} className="rounded-lg" data-testid="btn-restart">
            Nouvelle simulation
          </Button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// STEP: PROFIL
// ═══════════════════════════════════════
function StepProfil({ profil, setProfil, periodeMPRAvant, setPeriodeMPRAvant }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <User className="w-5 h-5 text-primary" />
          Profil du client
        </h2>
        <div className="space-y-3" data-testid="profile-selection">
          {PROFILES.map(p => (
            <button
              key={p.id}
              onClick={() => setProfil(p.id)}
              data-testid={`profile-${p.id}`}
              className={cn(
                "w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left",
                profil === p.id
                  ? "border-primary bg-primary/5 shadow-md"
                  : "border-border hover:border-primary/40 hover:bg-muted/50"
              )}
            >
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", profil === p.id ? "bg-primary text-white" : "bg-muted text-muted-foreground")}>
                <p.icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{p.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Ordre : {p.order[0]} puis {p.order[1]}
                </p>
              </div>
              {profil === p.id && <Check className="w-5 h-5 text-primary" />}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <Calendar className="w-5 h-5 text-primary" />
          Période MPR
        </h2>
        <button
          type="button"
          onClick={() => setPeriodeMPRAvant(!periodeMPRAvant)}
          className={cn("flex items-center gap-4 w-full p-4 rounded-lg border-2 transition-all", periodeMPRAvant ? "border-amber-400 bg-amber-500/10" : "border-primary bg-primary/5")}
          data-testid="switch-periode-mpr"
        >
          {periodeMPRAvant ? <ToggleRight className="w-7 h-7 text-amber-500" /> : <ToggleLeft className="w-7 h-7 text-primary" />}
          <div className="text-left flex-1">
            <p className="font-semibold">{periodeMPRAvant ? "Avant 31/12/25" : "Après 31/12/25"}</p>
          </div>
        </button>

        {profil && (
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm text-blue-800" data-testid="profile-period-info">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>{PROFILES.find(p => p.id === profil)?.label}</strong> — {periodeMPRAvant ? "Avant 31/12/25" : "Après 31/12/25"}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// STEP: INFORMATIONS DU BIEN
// ═══════════════════════════════════════
function StepBien({ surface, setSurface, dpeActuel, setDpeActuel, dpeVise, setDpeVise, zone, setZone, chauffage, setChauffage, precarite, setPrecarite, ageLogement, setAgeLogement, elig174, eligMPR, programOrder }) {
  const classJump = getDPEJump(dpeActuel, dpeVise);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Col 1: Info du bien */}
      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <Home className="w-5 h-5 text-primary" />
          Le bien
        </h2>
        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-2"><Ruler className="w-4 h-4" /> Surface habitable (m²)</Label>
            <Input type="number" value={surface} onChange={e => setSurface(parseInt(e.target.value) || 0)} className="rounded-lg mt-1.5" data-testid="input-surface" />
            <p className="text-xs text-muted-foreground mt-1">Coefficient 174 : {getCoefficient(surface)}</p>
          </div>
          <div>
            <Label className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Âge du logement</Label>
            <Select value={ageLogement} onValueChange={setAgeLogement}>
              <SelectTrigger className="rounded-lg mt-1.5" data-testid="select-age"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="less_than_2">Moins de 2 ans</SelectItem>
                <SelectItem value="2_to_15">Entre 2 et 15 ans</SelectItem>
                <SelectItem value="more_than_15">Plus de 15 ans</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Zone climatique</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="rounded-lg mt-1.5" data-testid="select-zone"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="H1">H1 - Nord et Est</SelectItem>
                <SelectItem value="H2">H2 - Ouest et Centre</SelectItem>
                <SelectItem value="H3">H3 - Sud et Méditerranée</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-2"><ThermometerSun className="w-4 h-4" /> Type de chauffage actuel</Label>
            <Select value={chauffage} onValueChange={setChauffage}>
              <SelectTrigger className="rounded-lg mt-1.5" data-testid="select-chauffage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Gaz"><span className="flex items-center gap-2"><Flame className="w-4 h-4 text-orange-500" />Gaz</span></SelectItem>
                <SelectItem value="Fioul"><span className="flex items-center gap-2"><Flame className="w-4 h-4 text-amber-700" />Fioul</span></SelectItem>
                <SelectItem value="Elec"><span className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-500" />Électrique</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Précarité énergétique</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <Button type="button" variant={precarite === "bleu" ? "default" : "outline"} onClick={() => setPrecarite("bleu")} className={cn("rounded-lg", precarite === "bleu" && "bg-blue-500 hover:bg-blue-600")} data-testid="precarite-bleu">
                Bleu (Très modeste)
              </Button>
              <Button type="button" variant={precarite === "jaune" ? "default" : "outline"} onClick={() => setPrecarite("jaune")} className={cn("rounded-lg", precarite === "jaune" && "bg-amber-500 hover:bg-amber-600")} data-testid="precarite-jaune">
                Jaune (Modeste)
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Col 2: DPE */}
      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <ThermometerSun className="w-5 h-5 text-primary" />
          DPE (Diagnostic de Performance)
        </h2>
        <div className="space-y-4">
          <div>
            <Label>DPE actuel</Label>
            <div className="grid grid-cols-7 gap-1.5 mt-2" data-testid="dpe-actuel-grid">
              {DPE_CLASSES.map(c => (
                <button key={c} onClick={() => setDpeActuel(c)} data-testid={`dpe-actuel-${c}`}
                  className={cn("py-2.5 rounded-lg text-sm font-bold text-white transition-all", DPE_COLORS[c], dpeActuel === c ? "ring-2 ring-offset-2 ring-primary scale-110 shadow-lg" : "opacity-60 hover:opacity-90")}
                >{c}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>DPE visé (après travaux)</Label>
            <div className="grid grid-cols-7 gap-1.5 mt-2" data-testid="dpe-vise-grid">
              {DPE_CLASSES.map(c => {
                const disabled = DPE_CLASSES.indexOf(c) >= DPE_CLASSES.indexOf(dpeActuel);
                return (
                  <button key={c} onClick={() => !disabled && setDpeVise(c)} disabled={disabled} data-testid={`dpe-vise-${c}`}
                    className={cn("py-2.5 rounded-lg text-sm font-bold text-white transition-all", DPE_COLORS[c], disabled ? "opacity-20 cursor-not-allowed" : dpeVise === c ? "ring-2 ring-offset-2 ring-primary scale-110 shadow-lg" : "opacity-60 hover:opacity-90")}
                  >{c}</button>
                );
              })}
            </div>
          </div>
          {classJump > 0 && (
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="dpe-jump-info">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-800">Saut de {classJump} classe{classJump > 1 ? "s" : ""}</p>
                  <p className="text-xs text-emerald-700">{dpeActuel} <ArrowRight className="w-3 h-3 inline" /> {dpeVise}</p>
                </div>
              </div>
            </div>
          )}
          {classJump <= 0 && (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="dpe-jump-warning">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-amber-800">Le DPE visé doit être meilleur que le DPE actuel (minimum 2 classes d'écart pour la 174).</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Col 3: Éligibilité */}
      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <Check className="w-5 h-5 text-primary" />
          Éligibilité
        </h2>
        <div className="space-y-4">
          {programOrder.map((prog, i) => {
            const elig = prog === "174" ? elig174 : eligMPR;
            return (
              <div key={prog} className={cn("p-4 rounded-lg border-2 transition-all", elig.eligible ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/30 bg-red-500/10")} data-testid={`elig-${prog}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={elig.eligible ? "default" : "destructive"} className="text-xs">
                      {i + 1}{i === 0 ? "er" : "e"}
                    </Badge>
                    <span className="font-semibold">Programme {prog}</span>
                  </div>
                  {elig.eligible ? (
                    <Check className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  )}
                </div>
                <p className={cn("text-sm", elig.eligible ? "text-emerald-700" : "text-red-700")}>{elig.reason}</p>
              </div>
            );
          })}
        </div>

        {!elig174.eligible && !eligMPR.eligible && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-sm text-red-800 font-medium">Aucun programme n'est éligible avec cette configuration. Vous pouvez toujours continuer pour voir le récapitulatif.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// STEP: PROGRAMME 174
// ═══════════════════════════════════════
function Step174({ surface, scenario174, setScenario174, valorisation, setValorisation, quantites, setQuantites, couts174, setCouts174, results174, dpeActuel, dpeVise, isCreateMode, regie174, regieMode, setRegieMode, isJaune, precarite }) {
  const classJump = getDPEJump(dpeActuel, dpeVise);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <Settings className="w-5 h-5 text-primary" />
          Configuration 174
        </h2>
        <div className="space-y-4">
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <Label className="flex items-center gap-2 text-amber-800 font-semibold"><Euro className="w-4 h-4" /> Valorisation CEE (€/MWh cumac)</Label>
            <Input type="number" value={valorisation} onChange={e => setValorisation(parseFloat(e.target.value) || 0)} className="w-full rounded-lg mt-2 text-lg font-bold text-center" step="0.5" min="0" data-testid="input-valorisation" />
          </div>
          <div>
            <Label>Nombre de classes (scénario)</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {[2, 3, 4].map(n => (
                <Button key={n} type="button" variant={scenario174 === n ? "default" : "outline"} onClick={() => setScenario174(n)} className="rounded-lg" data-testid={`scenario-${n}`}>
                  {n} classes
                </Button>
              ))}
            </div>
            {classJump >= 2 && (
              <p className="text-xs text-emerald-600 mt-1">DPE {dpeActuel} vers {dpeVise} = {classJump} classes d'écart</p>
            )}
          </div>
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <Settings className="w-5 h-5 text-primary" />
          Travaux par logement
        </h2>
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2">
          {Object.entries(TRAVAUX_DEFAUT).map(([key, travail]) => (
            <div key={key} className="flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{travail.label}</p>
                {quantites[key] > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {((couts174[key].materiel + couts174[key].pose) * quantites[key]).toLocaleString("fr-FR")} €
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" value={quantites[key]} onChange={e => setQuantites(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))} className="w-20 rounded-lg text-center" min="0" data-testid={`qty-${key}`} />
                {travail.unite && <span className="text-xs text-muted-foreground w-10">{travail.unite}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <TrendingUp className="w-5 h-5 text-primary" />
          Résultats 174
        </h2>
        {results174 ? (
          <div className="space-y-4">
            <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">CA TTC</span>
                <span className="text-xl font-bold text-primary" data-testid="result-174-cattc">{results174.caTTC.toLocaleString("fr-FR")} €</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{results174.mwhCumac} MWh x {results174.coef} x {valorisation} €</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 border border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">CA HT (TVA 5.5%)</span>
                <span className="font-medium">{results174.caHT.toLocaleString("fr-FR")} €</span>
              </div>
            </div>
            {!isCreateMode && (
            <>
            <div className="p-3 rounded-lg bg-muted/50 border border-border">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Mode coût</span>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <Button type="button" variant={regieMode === "regie" ? "default" : "outline"} onClick={() => setRegieMode("regie")} className="rounded-lg text-xs h-7 px-2" size="sm" data-testid="mode-regie-174">Régie</Button>
                    <Button type="button" variant={regieMode === "lead" ? "default" : "outline"} onClick={() => setRegieMode("lead")} className="rounded-lg text-xs h-7 px-2" size="sm" data-testid="mode-lead-174">Lead</Button>
                  </div>
                  <span className="font-medium text-sm">{regie174.toLocaleString('fr-FR')} €</span>
                </div>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="flex justify-between text-sm">
                <span className="text-red-700">Coût travaux (dont régie)</span>
                <span className="font-medium text-red-700">-{results174.coutTravaux.toLocaleString("fr-FR")} €</span>
              </div>
            </div>
            <div className={cn("p-5 rounded-lg border-2", results174.marge >= 0 ? "bg-emerald-500/10 border-emerald-500/40" : "bg-red-500/10 border-red-300")}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {results174.marge >= 0 ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
                  <span className="font-semibold">MARGE 174</span>
                </div>
                <span className={cn("text-2xl font-bold", results174.marge >= 0 ? "text-emerald-700" : "text-red-700")} data-testid="result-174-marge">
                  {results174.marge.toLocaleString("fr-FR")} €
                </span>
              </div>
            </div>
            </>
            )}
          </div>
        ) : (
          <div className="p-4 bg-muted/30 rounded-lg text-sm text-muted-foreground">Configurez les paramètres pour voir les résultats.</div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// STEP: PROGRAMME MPR
// ═══════════════════════════════════════
function StepMPR({ zone, surface, precarite, setPrecarite, typeChantier, setTypeChantier, chargesMPR, setChargesMPR, resultsMPR, isCreateMode, regieMPR, regieMode, setRegieMode, periode }) {
  const handleChargeChange = (key, value) => setChargesMPR(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <Home className="w-5 h-5 text-primary" />
          Configuration MPR
        </h2>
        <div className="space-y-4">
          <div>
            <Label>Type de chantier</Label>
            <Select value={typeChantier} onValueChange={setTypeChantier}>
              <SelectTrigger className="rounded-lg mt-1.5" data-testid="select-type-chantier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PAC">PAC seule</SelectItem>
                <SelectItem value="PAC/SSC">PAC + SSC</SelectItem>
                <SelectItem value="PAC + CESI">PAC + CESI</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Précarité énergétique</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <Button type="button" variant={precarite === "bleu" ? "default" : "outline"} onClick={() => setPrecarite("bleu")} className={cn("rounded-lg", precarite === "bleu" && "bg-blue-500 hover:bg-blue-600")} data-testid="mpr-precarite-bleu">
                Bleu
              </Button>
              <Button type="button" variant={precarite === "jaune" ? "default" : "outline"} onClick={() => setPrecarite("jaune")} className={cn("rounded-lg", precarite === "jaune" && "bg-amber-500 hover:bg-amber-600")} data-testid="mpr-precarite-jaune">
                Jaune
              </Button>
            </div>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-sm">
            <p><strong>Zone :</strong> {zone}</p>
            <p><strong>Surface :</strong> {surface} m²</p>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <Euro className="w-5 h-5 text-primary" />
          Charges ({typeChantier})
        </h2>
        <div className="space-y-4">
          <div>
            <Label>Marchandise (€)</Label>
            <Input type="number" className="rounded-lg mt-1.5" data-testid="mpr-marchandise"
              value={typeChantier === "PAC" ? chargesMPR.marchandise_pac : typeChantier === "PAC/SSC" ? chargesMPR.marchandise_pac_ssc : chargesMPR.marchandise_pac_cesi}
              onChange={e => handleChargeChange(typeChantier === "PAC" ? "marchandise_pac" : typeChantier === "PAC/SSC" ? "marchandise_pac_ssc" : "marchandise_pac_cesi", e.target.value)} />
          </div>
          <div>
            <Label>Sous-traitant (€)</Label>
            <Input type="number" className="rounded-lg mt-1.5" data-testid="mpr-sous-traitant"
              value={typeChantier === "PAC" ? chargesMPR.sous_traitant_pac : typeChantier === "PAC/SSC" ? chargesMPR.sous_traitant_pac_ssc : chargesMPR.sous_traitant_pac_cesi}
              onChange={e => handleChargeChange(typeChantier === "PAC" ? "sous_traitant_pac" : typeChantier === "PAC/SSC" ? "sous_traitant_pac_ssc" : "sous_traitant_pac_cesi", e.target.value)} />
          </div>
          <div>
            <Label>Mode de coût</Label>
            <div className="flex gap-1 mt-1.5">
              <Button type="button" variant={regieMode === "regie" ? "default" : "outline"} onClick={() => setRegieMode("regie")} className="rounded-lg text-xs" size="sm" data-testid="mode-regie-mpr">Régie</Button>
              <Button type="button" variant={regieMode === "lead" ? "default" : "outline"} onClick={() => setRegieMode("lead")} className="rounded-lg text-xs" size="sm" data-testid="mode-lead-mpr">Lead</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Régie MPR : {regieMPR.toLocaleString('fr-FR')} €</p>
          </div>
          <div>
            <Label>Admin (€)</Label>
            <Input type="number" value={chargesMPR.admin} onChange={e => handleChargeChange("admin", e.target.value)} className="rounded-lg mt-1.5" data-testid="mpr-admin" />
          </div>
        </div>
      </div>

      <div className="border border-border rounded-lg bg-card p-6 space-y-5">
        <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
          <TrendingUp className="w-5 h-5 text-primary" />
          Résultats MPR
        </h2>
        {resultsMPR ? (
          <div className="space-y-4">
            <div className={cn("p-4 rounded-lg border", precarite === "bleu" ? "bg-blue-500/10 border-blue-500/30" : "bg-amber-500/10 border-amber-500/30")}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">MPR (Ma Prime Rénov')</span>
                <span className={cn("text-xl font-bold", precarite === "bleu" ? "text-blue-700" : "text-amber-700")} data-testid="result-mpr-prime">
                  {resultsMPR.mpr.toLocaleString("fr-FR")} €
                </span>
              </div>
            </div>
            <div className={cn("p-4 rounded-lg border", precarite === "bleu" ? "bg-blue-500/10 border-blue-500/30" : "bg-amber-500/10 border-amber-500/30")}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">CEE</span>
                <span className={cn("text-xl font-bold", precarite === "bleu" ? "text-blue-700" : "text-amber-700")} data-testid="result-mpr-cee">
                  {resultsMPR.cee.toLocaleString("fr-FR")} €
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{resultsMPR.montantKwhc.toLocaleString("fr-FR")} kWhc x {PRIX_RACHAT[precarite]} €</p>
            </div>
            <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Total Aides</span>
                <span className="text-xl font-bold text-primary">{resultsMPR.totalAides.toLocaleString("fr-FR")} €</span>
              </div>
            </div>
            {!isCreateMode && (
            <>
            <div className="p-3 rounded-lg bg-muted/50 border border-border text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Financement MPR (12%)</span><span>-{resultsMPR.finMPR.toLocaleString("fr-FR")} €</span></div>
            </div>
            <div className={cn("p-5 rounded-lg border-2", resultsMPR.marge >= 0 ? "bg-emerald-500/10 border-emerald-500/40" : "bg-red-500/10 border-red-300")}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {resultsMPR.marge >= 0 ? <TrendingUp className="w-5 h-5 text-emerald-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
                  <span className="font-semibold">MARGE MPR</span>
                </div>
                <span className={cn("text-2xl font-bold", resultsMPR.marge >= 0 ? "text-emerald-700" : "text-red-700")} data-testid="result-mpr-marge">
                  {resultsMPR.marge.toLocaleString("fr-FR")} €
                </span>
              </div>
            </div>
            </>
            )}
          </div>
        ) : (
          <div className="p-4 bg-muted/30 rounded-lg text-sm text-muted-foreground">Configurez les paramètres pour voir les résultats.</div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// STEP: RECAPITULATIF
// ═══════════════════════════════════════
function StepRecap({ profil, periode, surface, dpeActuel, dpeVise, zone, chauffage, precarite, ageLogement, elig174, eligMPR, results174, resultsMPR, programOrder, typeChantier, valorisation, scenario174, quantites, couts174, chargesMPR, regieMPR, regieMode, isCreateMode }) {
  const profileLabel = PROFILES.find(p => p.id === profil)?.label || profil;
  const periodeLabel = PERIODS.find(p => p.id === periode)?.label || periode;
  const ageLabels = { less_than_2: "Moins de 2 ans", "2_to_15": "Entre 2 et 15 ans", more_than_15: "Plus de 15 ans" };
  const classJump = getDPEJump(dpeActuel, dpeVise);

  const totalMarge = (results174?.marge || 0) + (resultsMPR?.marge || 0);
  const totalCA = (results174?.caTTC || 0) + (resultsMPR?.totalAides || 0);

  let totalCoutTravaux174 = 0;
  Object.keys(quantites).forEach(key => {
    if (quantites[key] > 0) totalCoutTravaux174 += (couts174[key].materiel + couts174[key].pose) * quantites[key];
  });

  return (
    <div className="space-y-6">
      {/* Bannière résultats combinés */}
      <div className={cn("p-6 rounded-lg border-2", totalMarge >= 0 ? "bg-emerald-500/10 border-emerald-500/40" : "bg-red-500/10 border-red-300")} data-testid="recap-banner">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <h2 className="text-2xl font-bold">Récapitulatif complet</h2>
            <p className="text-muted-foreground">{profileLabel}</p>
          </div>
          <div className="text-center sm:text-right">
            <p className="text-sm text-muted-foreground">Marge totale combinée</p>
            <p className={cn("text-4xl font-bold", totalMarge >= 0 ? "text-emerald-700" : "text-red-700")} data-testid="recap-total-marge">
              {totalMarge.toLocaleString("fr-FR")} €
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonne gauche : Résumé des saisies */}
        <div className="space-y-6">
          <div className="border border-border rounded-lg bg-card p-6" data-testid="recap-client-info">
            <h3 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              Informations saisies
            </h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Profil</p>
                <p className="font-medium">{profileLabel}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Période</p>
                <p className="font-medium">{periodeLabel}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Surface</p>
                <p className="font-medium">{surface} m² (coef. {getCoefficient(surface)})</p>
              </div>
              <div>
                <p className="text-muted-foreground">Âge du logement</p>
                <p className="font-medium">{ageLabels[ageLogement]}</p>
              </div>
              <div>
                <p className="text-muted-foreground">DPE</p>
                <p className="font-medium">
                  <span className={cn("inline-block w-6 h-6 rounded text-white text-xs font-bold text-center leading-6 mr-1", DPE_COLORS[dpeActuel])}>{dpeActuel}</span>
                  <ArrowRight className="w-3 h-3 inline mx-1" />
                  <span className={cn("inline-block w-6 h-6 rounded text-white text-xs font-bold text-center leading-6 ml-1", DPE_COLORS[dpeVise])}>{dpeVise}</span>
                  <span className="ml-2 text-muted-foreground">({classJump} classe{classJump > 1 ? "s" : ""})</span>
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Zone climatique</p>
                <p className="font-medium">{zone}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Chauffage</p>
                <p className="font-medium">{chauffage}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Précarité</p>
                <p className="font-medium">{precarite === "bleu" ? "Bleu (Très modeste)" : "Jaune (Modeste)"}</p>
              </div>
            </div>
          </div>

          {/* Résumé travaux 174 */}
          {elig174.eligible && (
            <div className="border border-border rounded-lg bg-card p-6" data-testid="recap-174-travaux">
              <h3 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3 mb-4">
                <Settings className="w-5 h-5 text-primary" />
                Travaux 174
              </h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between text-muted-foreground mb-2">
                  <span>Valorisation CEE : <strong className="text-foreground">{valorisation} €/MWh</strong></span>
                  <span>Scénario : <strong className="text-foreground">{scenario174} classes</strong></span>
                </div>
                {Object.entries(quantites).filter(([, v]) => v > 0).map(([key, qty]) => (
                  <div key={key} className="flex justify-between py-1 border-b border-border/30">
                    <span>{TRAVAUX_DEFAUT[key].label} x {qty}</span>
                    <span className="font-medium">{((couts174[key].materiel + couts174[key].pose) * qty).toLocaleString("fr-FR")} €</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 font-semibold">
                  <span>Total travaux 174</span>
                  <span className="text-red-600">{totalCoutTravaux174.toLocaleString("fr-FR")} €</span>
                </div>
              </div>
            </div>
          )}

          {/* Résumé charges MPR */}
          {eligMPR.eligible && (
            <div className="border border-border rounded-lg bg-card p-6" data-testid="recap-mpr-charges">
              <h3 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3 mb-4">
                <Euro className="w-5 h-5 text-primary" />
                Charges MPR ({typeChantier})
              </h3>
              <div className="text-sm space-y-1">
                <div className="flex justify-between py-1 border-b border-border/30">
                  <span>Marchandise</span>
                  <span className="font-medium">{(typeChantier === "PAC" ? chargesMPR.marchandise_pac : typeChantier === "PAC/SSC" ? chargesMPR.marchandise_pac_ssc : chargesMPR.marchandise_pac_cesi).toLocaleString("fr-FR")} €</span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/30">
                  <span>Sous-traitant</span>
                  <span className="font-medium">{(typeChantier === "PAC" ? chargesMPR.sous_traitant_pac : typeChantier === "PAC/SSC" ? chargesMPR.sous_traitant_pac_ssc : chargesMPR.sous_traitant_pac_cesi).toLocaleString("fr-FR")} €</span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/30">
                  <span>Régie MPR</span>
                  <span className="font-medium">{(regieMPR || 2500).toLocaleString("fr-FR")} €</span>
                </div>
                <div className="flex justify-between py-1 border-b border-border/30">
                  <span>Admin</span>
                  <span className="font-medium">{chargesMPR.admin.toLocaleString("fr-FR")} €</span>
                </div>
                <div className="flex justify-between pt-2 font-semibold">
                  <span>Total charges MPR</span>
                  <span className="text-red-600">{resultsMPR?.totalCharges?.toLocaleString("fr-FR")} €</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Colonne droite : Résultats */}
        <div className="space-y-6">
          {programOrder.map(prog => {
            const elig = prog === "174" ? elig174 : eligMPR;
            const results = prog === "174" ? results174 : resultsMPR;
            return (
              <div key={prog} className={cn("border rounded-lg p-6", elig.eligible ? "border-border bg-card" : "border-red-500/30 bg-red-500/10")} data-testid={`recap-result-${prog}`}>
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                  <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-primary" />
                    Programme {prog}
                  </h3>
                  <Badge variant={elig.eligible ? "default" : "destructive"}>
                    {elig.eligible ? "Éligible" : "Non éligible"}
                  </Badge>
                </div>
                {!elig.eligible ? (
                  <p className="text-sm text-red-700">{elig.reason}</p>
                ) : prog === "174" && results174 ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">MWh cumac</span><span className="font-medium">{results174.mwhCumac}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Coefficient surface</span><span className="font-medium">{results174.coef}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">CA TTC</span><span className="font-semibold text-primary">{results174.caTTC.toLocaleString("fr-FR")} €</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">CA HT</span><span className="font-medium">{results174.caHT.toLocaleString("fr-FR")} €</span></div>
                    {!isCreateMode && (
                    <>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">dont régie</span><span className="font-medium text-red-500">-{(results174.regie || 0).toLocaleString("fr-FR")} €</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Coût travaux</span><span className="font-medium text-red-600">-{results174.coutTravaux.toLocaleString("fr-FR")} €</span></div>
                    <div className={cn("flex justify-between text-lg font-bold pt-2 border-t border-border", results174.marge >= 0 ? "text-emerald-700" : "text-red-700")}>
                      <span>Marge</span><span>{results174.marge.toLocaleString("fr-FR")} €</span>
                    </div>
                    </>
                    )}
                  </div>
                ) : prog === "MPR" && resultsMPR ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">MPR (Ma Prime Rénov')</span><span className="font-medium">{resultsMPR.mpr.toLocaleString("fr-FR")} €</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">CEE</span><span className="font-medium">{resultsMPR.cee.toLocaleString("fr-FR")} €</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total aides</span><span className="font-semibold text-primary">{resultsMPR.totalAides.toLocaleString("fr-FR")} €</span></div>
                    {!isCreateMode && (
                    <>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total charges</span><span className="font-medium text-red-600">-{resultsMPR.totalCharges.toLocaleString("fr-FR")} €</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Financement MPR (12%)</span><span className="font-medium">-{resultsMPR.finMPR.toLocaleString("fr-FR")} €</span></div>
                    <div className={cn("flex justify-between text-lg font-bold pt-2 border-t border-border", resultsMPR.marge >= 0 ? "text-emerald-700" : "text-red-700")}>
                      <span>Marge</span><span>{resultsMPR.marge.toLocaleString("fr-FR")} €</span>
                    </div>
                    </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun résultat disponible.</p>
                )}
              </div>
            );
          })}

          {/* Total combiné */}
          <div className={cn("p-6 rounded-lg border-2", isCreateMode ? "bg-primary/5 border-primary/30" : totalMarge >= 0 ? "bg-emerald-500/10 border-emerald-500/40" : "bg-red-500/10 border-red-300")} data-testid="recap-combined-total">
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">CA total combiné</span>
                <span className="font-semibold text-primary">{totalCA.toLocaleString("fr-FR")} €</span>
              </div>
              {!isCreateMode && (
              <>
              {elig174.eligible && results174 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Marge 174</span>
                  <span className={cn("font-medium", results174.marge >= 0 ? "text-emerald-600" : "text-red-600")}>{results174.marge.toLocaleString("fr-FR")} €</span>
                </div>
              )}
              {eligMPR.eligible && resultsMPR && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Marge MPR</span>
                  <span className={cn("font-medium", resultsMPR.marge >= 0 ? "text-emerald-600" : "text-red-600")}>{resultsMPR.marge.toLocaleString("fr-FR")} €</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-3 border-t border-emerald-500/40">
                <div className="flex items-center gap-2">
                  {totalMarge >= 0 ? <TrendingUp className="w-6 h-6 text-emerald-600" /> : <TrendingDown className="w-6 h-6 text-red-600" />}
                  <span className="text-lg font-bold">MARGE TOTALE</span>
                </div>
                <span className={cn("text-3xl font-bold", totalMarge >= 0 ? "text-emerald-700" : "text-red-700")}>
                  {totalMarge.toLocaleString("fr-FR")} €
                </span>
              </div>
              </>
              )}
              {isCreateMode && (
              <div className="flex justify-between items-center pt-3 border-t border-primary/30">
                <span className="text-lg font-bold">CA TOTAL</span>
                <span className="text-3xl font-bold text-primary">
                  {totalCA.toLocaleString("fr-FR")} €
                </span>
              </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Les résultats sont indicatifs et dépendent des données saisies. Vérifiez les conditions d'éligibilité avec votre organisme.
            </span>
          </div>
        </div>
      </div>

      {/* Frise chronologique */}
      {(() => {
        const isCumule = elig174.eligible && eligMPR.eligible;
        let detail;
        if (isCumule) detail = "cumule";
        else if (elig174.eligible) detail = "174";
        else if (eligMPR.eligible) detail = "mpr";
        else return null;

        const eligCumArg = isCumule ? { order: programOrder } : null;
        const { timelines, totalDays } = buildTimelinesForProgram({
          programType: detail,
          eligCumule: eligCumArg,
          elig174: elig174,
          eligMPR: eligMPR
        });
        return <TimelineDisplay timelines={timelines} totalDays={totalDays} />;
      })()}
    </div>
  );
}


// ═══════════════════════════════════════
// STEP: CREER LE DOSSIER
// ═══════════════════════════════════════
const PROFILE_TO_BACKEND = {
  bailleur_sci: "sci",
  proprietaire_occupant: "proprietaire_occupant",
  residence_secondaire: "bailleur"
};

function StepDossier({ profil, dossierForm, setDossierForm, profiles, elig174, eligMPR }) {
  const backendProfile = PROFILE_TO_BACKEND[profil];
  const profileDocs = profiles.find(p => p.key === backendProfile)?.documents || [];
  const isCumule = elig174.eligible && eligMPR.eligible;
  const programmeLabel = isCumule ? "Opération Cumulée (174 + MPR)" : elig174.eligible ? "174" : "MPR";
  const isBailleurSci = profil === "bailleur_sci";

  const handleChange = (field, value) => setDossierForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Colonne gauche : Formulaire bénéficiaire */}
      <div className="space-y-6">
        {/* Informations du bénéficiaire */}
        <div className="border border-border rounded-lg bg-card p-6 space-y-5">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
            <User className="w-5 h-5 text-primary" />
            Informations du bénéficiaire
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2"><User className="w-4 h-4" /> Nom complet *</Label>
              <Input value={dossierForm.client_name} onChange={e => handleChange("client_name", e.target.value)} placeholder="Nom et prénom du client" className="rounded-lg mt-1.5" data-testid="dossier-client-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                <Input type="email" value={dossierForm.client_email} onChange={e => handleChange("client_email", e.target.value)} placeholder="email@exemple.fr" className="rounded-lg mt-1.5" data-testid="dossier-client-email" />
              </div>
              <div>
                <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Téléphone</Label>
                <Input value={dossierForm.client_phone} onChange={e => handleChange("client_phone", e.target.value)} placeholder="06 12 34 56 78" className="rounded-lg mt-1.5" data-testid="dossier-client-phone" />
              </div>
            </div>
          </div>
        </div>

        {/* Informations du logement */}
        <div className="border border-border rounded-lg bg-card p-6 space-y-5">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
            <Home className="w-5 h-5 text-primary" />
            Informations du logement
          </h2>
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Adresse du logement</Label>
              <Input value={dossierForm.adresse_logement} onChange={e => handleChange("adresse_logement", e.target.value)} placeholder="123 rue de la Paix, 75000 Paris" className="rounded-lg mt-1.5" data-testid="dossier-adresse-logement" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Code postal</Label>
                <Input value={dossierForm.code_postal} onChange={e => handleChange("code_postal", e.target.value)} placeholder="75000" className="rounded-lg mt-1.5" data-testid="dossier-code-postal" />
              </div>
              <div>
                <Label>Type de logement</Label>
                <Select value={dossierForm.type_logements} onValueChange={v => handleChange("type_logements", v)}>
                  <SelectTrigger className="rounded-lg mt-1.5" data-testid="dossier-type-logements"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="appartement">Appartement</SelectItem>
                    <SelectItem value="maison">Maison</SelectItem>
                    <SelectItem value="mixte">Mixte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isBailleurSci && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre de logements</Label>
                  <Input type="number" value={dossierForm.nombre_logements} onChange={e => handleChange("nombre_logements", parseInt(e.target.value) || 1)} min="1" className="rounded-lg mt-1.5" data-testid="dossier-nb-logements" />
                </div>
                <div>
                  <Label>Nom de la résidence</Label>
                  <Input value={dossierForm.nom_residence} onChange={e => handleChange("nom_residence", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-nom-residence" />
                </div>
              </div>
            )}
            {isBailleurSci && (
              <>
                <div>
                  <Label>Adresse de la résidence</Label>
                  <Input value={dossierForm.adresse_residence} onChange={e => handleChange("adresse_residence", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-adresse-residence" />
                </div>
                <div>
                  <Label>Coordonnées gardien</Label>
                  <Input value={dossierForm.coordonnees_gardien} onChange={e => handleChange("coordonnees_gardien", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-gardien" />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Champs spécifiques bailleur SCI */}
        {isBailleurSci && (
          <div className="border border-border rounded-lg bg-card p-6 space-y-5">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
              <Building className="w-5 h-5 text-primary" />
              Organisme Bailleur / SCI
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Organisme bailleur</Label>
                <Input value={dossierForm.organisme_bailleur} onChange={e => handleChange("organisme_bailleur", e.target.value)} placeholder="Nom de la SCI" className="rounded-lg mt-1.5" data-testid="dossier-organisme" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom du représentant</Label>
                  <Input value={dossierForm.nom_representant} onChange={e => handleChange("nom_representant", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-representant" />
                </div>
                <div>
                  <Label>Tél. mobile représentant</Label>
                  <Input value={dossierForm.tel_representant} onChange={e => handleChange("tel_representant", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-tel-representant" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tél. fixe représentant</Label>
                  <Input value={dossierForm.fixe_representant} onChange={e => handleChange("fixe_representant", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-fixe-representant" />
                </div>
                <div>
                  <Label>Email représentant</Label>
                  <Input type="email" value={dossierForm.email_representant} onChange={e => handleChange("email_representant", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-email-representant" />
                </div>
              </div>
              <div>
                <Label>Adresse du bailleur</Label>
                <Input value={dossierForm.adresse_bailleur} onChange={e => handleChange("adresse_bailleur", e.target.value)} className="rounded-lg mt-1.5" data-testid="dossier-adresse-bailleur" />
              </div>
            </div>
          </div>
        )}

        <div className="border border-border rounded-lg bg-card p-6">
          <Label>Notes / Commentaires</Label>
          <Textarea value={dossierForm.notes} onChange={e => handleChange("notes", e.target.value)} placeholder="Informations complémentaires..." className="rounded-lg mt-1.5 min-h-[80px]" data-testid="dossier-notes" />
        </div>
      </div>

      {/* Colonne droite : Documents requis + Résumé */}
      <div className="space-y-6">
        {/* Type de programme */}
        <div className={cn("p-5 rounded-lg border-2", isCumule ? "border-violet-300 bg-violet-500/10" : "border-primary/30 bg-primary/5")} data-testid="dossier-programme-type">
          <div className="flex items-center gap-3">
            <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", isCumule ? "bg-violet-600 text-white" : "bg-primary text-white")}>
              <FolderPlus className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Type de dossier</p>
              <p className="text-xl font-bold" data-testid="dossier-programme-label">{programmeLabel}</p>
            </div>
          </div>
        </div>

        {/* Documents requis */}
        <div className="border border-border rounded-lg bg-card p-6 space-y-5" data-testid="dossier-documents-list">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
            <FileText className="w-5 h-5 text-primary" />
            Documents à demander
          </h2>
          {profileDocs.length > 0 ? (
            <div className="space-y-3">
              {profileDocs.map((doc, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                  <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{doc.name}</p>
                    {doc.description && <p className="text-xs text-muted-foreground mt-0.5">{doc.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Chargement des documents...</p>
          )}
        </div>

        {/* Info résumé */}
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm text-blue-800">
          <Info className="w-4 h-4 inline mr-2" />
          Une fois le dossier créé, vous serez redirigé vers sa page détaillée où vous pourrez suivre l'avancement des étapes et envoyer le lien de dépôt des documents au client.
        </div>
      </div>
    </div>
  );
}
